import {Injectable} from '@angular/core';
import {gql} from 'apollo-angular';
import {googleConversionSentVariables} from 'app/core/services/gql/googleConversionSent';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {YandexMetricaService} from 'app/core/services/yandex-metrica.service';
import {PosthogService} from 'app/core/services/analytics/posthog.service';

const googleConversionSentMutation = gql`
    mutation googleConversionSent($eventNames: [String!]!) {
        googleConversionSent(eventNames: $eventNames)
    }
`;

const LOCALSTORAGE_KEY = 'apd_tag_manager_sent_events';

@Injectable({
    providedIn: 'root',
})
export class ClientAnalyticsService {
    protected dataLayer: any[] =
        // @ts-ignore
        (window.dataLayer = window.dataLayer || []);

    constructor(
        private api: ApiService,
        private localStorage: LocalStorageService,
        private yaMetrica: YandexMetricaService,
        private posthog: PosthogService
    ) {}

    private gtag(event: string, type: string, payload: Record<string, string>) {
        this.dataLayer.push(arguments);
    }

    //    <!-- Event snippet for Appodeal Sign Up Completed (NEW) conversion page -->
    public sendSignUpConversion() {
        this.gtag('event', 'conversion', {send_to: 'AW-957603572/3tRFCLuA5NEBEPS9z8gD'});
    }

    sentEvents(events: string[]) {
        if (!events.length) {
            //if nothing to send - nothing to do
            return;
        }
        // filter sent from localstorage to prevent double sending from parallel tabs
        const toSend = this.filterSent(events);
        // sending an events to Yandex metric
        this.sentConversionsToYandexMetrica(toSend);
        // send
        this.sentConversionsToTagManager(toSend);

        this.sentConversionsToPosthog(toSend);
        // mark as sent
        this.markAsSent(events);
        // sent to serve
        // we should not worry about success on the request, we already store locally and prevent double sending from this browser
        this.markAsSentOnServer(events).then(
            () => {},
            e => console.warn(`[TagManagerService]`, e)
        );
    }

    private filterSent(events: string[]): string[] {
        const sent = new Set(this.getLocalEvents());
        return events.filter(e => !sent.has(e));
    }

    private getLocalEvents() {
        const events = this.localStorage.getJson(LOCALSTORAGE_KEY);
        return Array.isArray(events) ? events : [];
    }

    private setSentLocalEvent(eventNames: string[]) {
        // deduplicate and store
        this.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify([...new Set([...this.getLocalEvents(), ...eventNames])]));
    }

    private sentConversionsToTagManager(events: string[]) {
        events.forEach(event => {
            if (!this.dataLayer.some(e => e.event === event)) {
                this.dataLayer.push({event});
            }
        });
    }

    private sentConversionsToYandexMetrica(events: string[]) {
        events.forEach(event => this.yaMetrica.sendEvent(event));
    }

    private sentConversionsToPosthog(events: string[]) {
        events.forEach(event => this.posthog.logEvent(event));
    }

    private markAsSent(eventNames: string[]) {
        this.setSentLocalEvent(eventNames);
    }

    private markAsSentOnServer(eventNames: string[]): Promise<any> {
        return this.api
            .mutate<any, googleConversionSentVariables>({
                mutation: googleConversionSentMutation,
                context: {
                    disableErrorNotifier: true,
                },
                variables: {eventNames},
            })
            .toPromise();
    }
}
